import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import {
  HiOutlinePlus, HiOutlineTrash, HiOutlineChevronRight, HiOutlineChevronDown,
  HiOutlineCheck, HiOutlinePencil
} from 'react-icons/hi';
import AssigneePicker from './AssigneePicker';
import AttachmentList from './AttachmentList';
import { hasCardAction } from '../../../utils/permissionHelper';

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed', 'Blocked'];
const statusColor = { Pending: 'yellow', 'In Progress': 'blue', Completed: 'green', Blocked: 'red' };

const normalizeAssigneeArr = (arr = []) =>
  arr.map(a => a.userId
    ? { userId: typeof a.userId === 'object' ? a.userId._id : a.userId }
    : { externalName: a.externalName }
  );

const DailyTracker = ({ missions, systemUsers = [], lockState = 'active', onUpdate, onRollup, user }) => {
  const canEdit = user?.role === 'system_admin' || hasCardAction(user?.permissions, 'vision', 'edit');
  const canCreate = user?.role === 'system_admin' || hasCardAction(user?.permissions, 'vision', 'create');
  const [tasks, setTasks] = useState([]);
  const [allActionItems, setAllActionItems] = useState([]);
  const [goalsByMission, setGoalsByMission] = useState({});
  const [aisByGoal, setAisByGoal] = useState({});
  const [loading, setLoading] = useState(true);

  const [filterMission, setFilterMission] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [expandedTask, setExpandedTask] = useState(null);
  const [addingTaskFor, setAddingTaskFor] = useState(null);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterMission) params.append('missionId', filterMission);
      if (filterDate)    params.append('date', filterDate);
      if (filterAssignee) params.append('assigneeId', filterAssignee);
      // Mirror the board's SHOW switch, or an admin's tracker keeps listing tasks
      // from locked missions that are absent from the mission list above it.
      params.append('state', lockState);
      const { data } = await axiosInstance.get(`/api/vision/tasks?${params}`);
      setTasks(data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterMission, filterDate, filterAssignee, lockState]);

  // Also load action items (filtered by mission, scoped to user's access on the backend)
  // so empty ones show up in the tree and the user can add tasks under them.
  const loadAllActionItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterMission) params.append('missionId', filterMission);
      params.append('state', lockState);
      const { data } = await axiosInstance.get(`/api/vision/action-items?${params}`);
      setAllActionItems(data.data || []);
    } catch (err) { console.error(err); }
  }, [filterMission, lockState]);

  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadAllActionItems(); }, [loadAllActionItems]);

  const loadGoals = useCallback(async (missionId) => {
    if (!missionId || goalsByMission[missionId]) return;
    try {
      const { data } = await axiosInstance.get(`/api/vision/goals?missionId=${missionId}`);
      setGoalsByMission(prev => ({ ...prev, [missionId]: data.data || [] }));
    } catch (err) { console.error(err); }
  }, [goalsByMission]);

  const loadActionItems = useCallback(async (goalId) => {
    if (!goalId || aisByGoal[goalId]) return;
    try {
      const { data } = await axiosInstance.get(`/api/vision/action-items?goalId=${goalId}`);
      setAisByGoal(prev => ({ ...prev, [goalId]: data.data || [] }));
    } catch (err) { console.error(err); }
  }, [aisByGoal]);

  // Group: Mission → Goal → ActionItem → Tasks
  // Seed from action items first so AIs with zero tasks still appear,
  // then attach tasks under their parent AI.
  const grouped = useMemo(() => {
    const tree = {};
    allActionItems.forEach(ai => {
      const mid = ai.mission?._id || ai.mission;
      const gid = ai.goal?._id || ai.goal;
      const aid = ai._id;
      if (!mid || !gid) return;
      if (!tree[mid]) tree[mid] = { mission: ai.mission, goals: {} };
      if (!tree[mid].goals[gid]) tree[mid].goals[gid] = { goal: ai.goal, items: {} };
      if (!tree[mid].goals[gid].items[aid]) {
        tree[mid].goals[gid].items[aid] = { actionItem: ai, tasks: [] };
      }
    });
    tasks.forEach(t => {
      const mid = t.mission?._id || t.mission;
      const gid = t.goal?._id || t.goal;
      const aid = t.actionItem?._id || t.actionItem;
      if (!tree[mid]) tree[mid] = { mission: t.mission, goals: {} };
      if (!tree[mid].goals[gid]) tree[mid].goals[gid] = { goal: t.goal, items: {} };
      if (!tree[mid].goals[gid].items[aid]) tree[mid].goals[gid].items[aid] = { actionItem: t.actionItem, tasks: [] };
      tree[mid].goals[gid].items[aid].tasks.push(t);
    });
    return Object.values(tree);
  }, [tasks, allActionItems]);

  const handleStatusChange = async (task, status) => {
    if (status === 'Completed') {
      const subs = task.subtasks || [];
      const pending = subs.filter(s => !s.isDone).length;
      if (pending > 0) {
        const ok = window.confirm(
          `Mark task as Completed?\n\nThis will also tick ${pending} pending subtask(s).`
        );
        if (!ok) return;
      }
    }
    try {
      const { data } = await axiosInstance.patch(`/api/vision/tasks/${task._id}/status`, { status });
      onRollup?.(data.rollup);
      await loadTasks();
      onUpdate?.();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || 'Failed to update status';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axiosInstance.delete(`/api/vision/tasks/${id}`);
      setTasks(prev => prev.filter(t => t._id !== id));
      onUpdate?.();
    } catch (err) { console.error(err); }
  };

  const refreshAll = useCallback(() => {
    loadTasks();
    loadAllActionItems();
  }, [loadTasks, loadAllActionItems]);

  const handleAddSubtask = async (taskId, title) => {
    if (!title.trim()) return;
    try {
      const { data } = await axiosInstance.post(`/api/vision/tasks/${taskId}/subtasks`, { title });
      onRollup?.(data.rollup);
      setTasks(prev => prev.map(t => t._id === taskId ? data.data : t));
      onUpdate?.();
    } catch (err) { console.error(err); }
  };

  const handleToggleSubtask = async (taskId, subId) => {
    try {
      const { data } = await axiosInstance.patch(`/api/vision/tasks/${taskId}/subtasks/${subId}/toggle`);
      onRollup?.(data.rollup);
      setTasks(prev => prev.map(t => t._id === taskId ? data.data : t));
      onUpdate?.();
    } catch (err) { console.error(err); }
  };

  const handleDeleteSubtask = async (taskId, subId) => {
    try {
      const { data } = await axiosInstance.delete(`/api/vision/tasks/${taskId}/subtasks/${subId}`);
      onRollup?.(data.rollup);
      setTasks(prev => prev.map(t => t._id === taskId ? data.data : t));
      onUpdate?.();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="v26-section-header" data-tour="daily-header">
        <span className="v26-section-title">Daily Tracker</span>
        {canCreate && (
          <button className="v26-btn v26-btn--primary" onClick={() => setShowAdd(s => !s)}>
            <HiOutlinePlus size={16} /> Add Task
          </button>
        )}
      </div>

      {showAdd && (
        <AddTaskForm
          missions={missions}
          systemUsers={systemUsers}
          goalsByMission={goalsByMission}
          aisByGoal={aisByGoal}
          onLoadGoals={loadGoals}
          onLoadActionItems={loadActionItems}
          onCancel={() => setShowAdd(false)}
          onCreated={(newTask) => {
            setTasks(prev => [newTask, ...prev]);
            setShowAdd(false);
            onUpdate?.();
          }}
        />
      )}

      <div className="v26-filters">
        <select value={filterMission} onChange={e => setFilterMission(e.target.value)}>
          <option value="">All Missions</option>
          {missions.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
        <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)} />
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All Assignees</option>
          {systemUsers.map(u => (
            <option key={u._id} value={u._id}>{u.fullName || u.username}</option>
          ))}
        </select>
        {(filterMission || filterDate || filterAssignee) && (
          <button className="v26-btn v26-btn--secondary v26-btn--sm"
            onClick={() => { setFilterMission(''); setFilterDate(''); setFilterAssignee(''); }}>
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <div className="v26-empty">Loading tasks…</div>
      ) : grouped.length === 0 ? (
        <div className="v26-empty">Nothing to show. Create action items in the Roadmap, then add tasks here.</div>
      ) : (
        grouped.map(missionGroup => (
          <div key={missionGroup.mission?._id || 'unknown'} className="v26-panel" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mission</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                {missionGroup.mission?.name || 'Unknown Mission'}
              </span>
            </div>

            {Object.values(missionGroup.goals).map(goalGroup => (
              <div key={goalGroup.goal?._id || 'unkg'} style={{ marginBottom: 14, paddingLeft: 8 }}>
                <div style={{ marginBottom: 6, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Goal</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>
                    {goalGroup.goal?.name || 'Unknown Goal'}
                  </span>
                </div>

                {Object.values(goalGroup.items).map(({ actionItem, tasks: aTasks }) => (
                  <div key={actionItem?._id || 'unk'} style={{
                    borderLeft: '3px solid #a8336b',
                    paddingLeft: 12, marginBottom: 10
                  }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Action Item</span>
                      <span>▸ {actionItem?.title || 'Unknown Action Item'}</span>
                      {actionItem?.deadline && (
                        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>
                          due {new Date(actionItem.deadline).toLocaleDateString('en-GB')}
                        </span>
                      )}
                      {aTasks.length === 0 && (
                        <span style={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>no tasks yet</span>
                      )}
                      {canCreate && actionItem?._id && addingTaskFor !== actionItem._id && (
                        <button
                          onClick={() => setAddingTaskFor(actionItem._id)}
                          className="v26-btn v26-btn--secondary v26-btn--sm"
                          style={{ fontSize: 10, padding: '2px 8px', marginLeft: 'auto' }}
                        >
                          <HiOutlinePlus size={11} /> Task
                        </button>
                      )}
                    </div>

                    {addingTaskFor === actionItem?._id && (
                      <InlineAddTask
                        actionItemId={actionItem._id}
                        systemUsers={systemUsers}
                        onCancel={() => setAddingTaskFor(null)}
                        onCreated={() => { setAddingTaskFor(null); refreshAll(); onUpdate?.(); }}
                      />
                    )}

                    {aTasks.map(task => (
                      <TaskRow
                        key={task._id}
                        task={task}
                        expanded={expandedTask === task._id}
                        onToggleExpand={() => setExpandedTask(prev => prev === task._id ? null : task._id)}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                        onAddSubtask={handleAddSubtask}
                        onToggleSubtask={handleToggleSubtask}
                        onDeleteSubtask={handleDeleteSubtask}
                        onRefresh={refreshAll}
                        canEdit={canEdit}
                        systemUsers={systemUsers}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

const EditTaskInlineDT = ({ task, systemUsers, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    deadline: task.deadline ? new Date(task.deadline).toISOString().slice(0, 10) : '',
    assignee: normalizeAssigneeArr(task.assignee)
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/vision/tasks/${task._id}`, {
        title: form.title.trim(),
        description: form.description,
        deadline: form.deadline || null,
        assignee: form.assignee
      });
      onSaved();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to update task');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ background: '#f8fafc', border: '1px dashed #94a3b8', borderRadius: 8, padding: 10, marginBottom: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>Edit Task</div>
      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="Task title…" autoFocus
        style={{ width: '100%', fontSize: 12, padding: '6px 9px', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, boxSizing: 'border-box' }} />
      <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
        placeholder="Description (optional)" rows={2}
        style={{ width: '100%', fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, fontFamily: 'inherit', boxSizing: 'border-box' }} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
        <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
          style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
        <div style={{ flex: 1, minWidth: 180 }}>
          <AssigneePicker value={form.assignee} onChange={(arr) => setForm(f => ({ ...f, assignee: arr }))}
            systemUsers={systemUsers} compact placeholder="Assignees…" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">Cancel</button>
        <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
          {saving ? '…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const SubtaskRowDT = ({ taskId, sub, onToggle, onDelete, onRenamed, canEdit }) => {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(sub.title);
  const saveRename = async () => {
    const t = draft.trim();
    if (!t) { setRenaming(false); setDraft(sub.title); return; }
    if (t === sub.title) { setRenaming(false); return; }
    try {
      const { data } = await axiosInstance.patch(`/api/vision/tasks/${taskId}/subtasks/${sub._id}`, { title: t });
      onRenamed(data.data);
      setRenaming(false);
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || 'Failed to rename subtask');
    }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 12 }}>
      <button
        onClick={() => onToggle(taskId, sub._id)}
        style={{
          width: 16, height: 16, borderRadius: 3,
          border: '1.5px solid ' + (sub.isDone ? '#16a34a' : '#cbd5e1'),
          background: sub.isDone ? '#16a34a' : '#fff',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
        }}
      >
        {sub.isDone && <HiOutlineCheck size={10} color="#fff" />}
      </button>
      {renaming ? (
        <>
          <input
            value={draft} autoFocus
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') saveRename();
              else if (e.key === 'Escape') { setRenaming(false); setDraft(sub.title); }
            }}
            style={{ flex: 1, fontSize: 12, padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: 4 }}
          />
          <button onClick={saveRename} style={{ background: '#10b981', color: '#fff', border: 'none', padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>Save</button>
          <button onClick={() => { setRenaming(false); setDraft(sub.title); }} style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '3px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>×</button>
        </>
      ) : (
        <>
          <span style={{
            flex: 1, color: sub.isDone ? '#94a3b8' : '#334155',
            textDecoration: sub.isDone ? 'line-through' : 'none'
          }}>{sub.title}</span>
          {canEdit && (
            <button onClick={() => setRenaming(true)} title="Rename subtask"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 2 }}>
              <HiOutlinePencil size={11} />
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => onDelete(taskId, sub._id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
            >
              <HiOutlineTrash size={11} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

const TaskRow = ({
  task, expanded, onToggleExpand,
  onStatusChange, onDelete,
  onAddSubtask, onToggleSubtask, onDeleteSubtask, onRefresh,
  canEdit, systemUsers
}) => {
  const [newSub, setNewSub] = useState('');
  const [editing, setEditing] = useState(false);
  const subs = task.subtasks || [];
  const completedSubs = subs.filter(s => s.isDone).length;

  if (editing) {
    return (
      <EditTaskInlineDT
        task={task}
        systemUsers={systemUsers}
        onSaved={() => { setEditing(false); onRefresh?.(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }
  const crumbs = [
    { label: 'Mission', value: task.mission?.name },
    { label: 'Goal', value: task.goal?.name },
    { label: 'Action', value: task.actionItem?.title },
  ].filter(c => c.value);

  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
      padding: '8px 10px', marginBottom: 6
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={onToggleExpand}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#64748b' }}>
          {expanded ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{task.title}</div>
          {crumbs.length > 0 && (
            <div style={{ fontSize: 10, marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
              {crumbs.map((c, i) => (
                <React.Fragment key={c.label}>
                  {i > 0 && <span style={{ color: '#cbd5e1' }}>›</span>}
                  <span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 3 }}>{c.label}</span>
                    <span style={{ color: '#a8336b', fontWeight: 600 }}>{c.value}</span>
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {task.deadline && <span>📅 {new Date(task.deadline).toLocaleDateString('en-GB')}</span>}
            {(task.assignee || []).length > 0 && (
              <span>👤 {(task.assignee || []).map(a => a.userId?.fullName || a.externalName || '?').join(', ')}</span>
            )}
            {subs.length > 0 && <span>☑ {completedSubs}/{subs.length}</span>}
            {task.completedAt && (
              <span style={{ color: '#16a34a' }}>✓ done {new Date(task.completedAt).toLocaleDateString('en-GB')}</span>
            )}
            {task.autoRisk && task.autoRisk !== 'Low' && (
              <span style={{ color: '#ef4444', fontWeight: 600 }}>⚠ {task.autoRisk}</span>
            )}
          </div>
        </div>

        <select
          className={`v26-badge v26-badge--${statusColor[task.status] || 'gray'}`}
          style={{ border: 'none', cursor: 'pointer', background: 'transparent', fontSize: 11 }}
          value={task.status}
          onChange={e => onStatusChange(task, e.target.value)}
        >
          {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
        </select>

        {canEdit && (
          <button className="v26-btn v26-btn--secondary v26-btn--sm" onClick={() => setEditing(true)} title="Edit">
            <HiOutlinePencil size={12} />
          </button>
        )}
        {canEdit && (
          <button className="v26-btn v26-btn--danger v26-btn--sm" onClick={() => onDelete(task._id)} title="Delete">
            <HiOutlineTrash size={12} />
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 8, paddingLeft: 22, borderTop: '1px dashed #e2e8f0', paddingTop: 8 }}>
          {subs.map(sub => (
            <SubtaskRowDT
              key={sub._id}
              taskId={task._id}
              sub={sub}
              onToggle={onToggleSubtask}
              onDelete={onDeleteSubtask}
              onRenamed={() => onRefresh?.()}
              canEdit={canEdit}
            />
          ))}

          {canEdit && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input
                value={newSub}
                onChange={e => setNewSub(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newSub.trim()) {
                    onAddSubtask(task._id, newSub);
                    setNewSub('');
                  }
                }}
                placeholder="Add subtask…"
                style={{ flex: 1, fontSize: 12, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }}
              />
              <button
                onClick={() => { if (newSub.trim()) { onAddSubtask(task._id, newSub); setNewSub(''); } }}
                className="v26-btn v26-btn--secondary v26-btn--sm"
                style={{ fontSize: 11 }}
              >
                Add
              </button>
            </div>
          )}
          <AttachmentList
            entityType="tasks"
            entityId={task._id}
            attachments={task.attachments || []}
            canEdit={canEdit}
            onChange={() => onRefresh?.()}
            compact
          />
        </div>
      )}
    </div>
  );
};

// Inline "+ Task" UI shown directly under an action item heading in the tracker.
const InlineAddTask = ({ actionItemId, systemUsers, onCancel, onCreated }) => {
  const [form, setForm] = useState({ title: '', deadline: '', assignee: [] });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await axiosInstance.post('/api/vision/tasks', {
        actionItem: actionItemId,
        title: form.title.trim(),
        deadline: form.deadline || undefined,
        assignee: form.assignee
      });
      onCreated();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to add task');
    } finally { setSaving(false); }
  };
  return (
    <div style={{ background: '#f8fafc', borderRadius: 6, padding: 8, marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
      <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        placeholder="Task title…" autoFocus
        onKeyDown={e => { if (e.key === 'Enter') submit(); else if (e.key === 'Escape') onCancel(); }}
        style={{ flex: 2, minWidth: 140, fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
      <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
        style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
      <div style={{ flex: 2, minWidth: 180 }}>
        <AssigneePicker value={form.assignee} onChange={(arr) => setForm(f => ({ ...f, assignee: arr }))}
          systemUsers={systemUsers} compact placeholder="Assignees…" />
      </div>
      <button onClick={submit} disabled={saving} className="v26-btn v26-btn--primary v26-btn--sm">
        {saving ? '…' : 'Add'}
      </button>
      <button onClick={onCancel} className="v26-btn v26-btn--secondary v26-btn--sm">×</button>
    </div>
  );
};

const AddTaskForm = ({
  missions, systemUsers, goalsByMission, aisByGoal,
  onLoadGoals, onLoadActionItems,
  onCancel, onCreated
}) => {
  const [form, setForm] = useState({
    mission: '', goal: '', actionItem: '', title: '', deadline: '',
    assignee: []
  });
  const [saving, setSaving] = useState(false);

  const goalList = form.mission ? (goalsByMission[form.mission] || []) : [];
  const aiList = form.goal ? (aisByGoal[form.goal] || []) : [];
  // A locked mission is a closed phase — the backend rejects writes into it with
  // a 423, so never offer one as a target here.
  const openMissions = missions.filter(m => !m.isLocked);

  useEffect(() => { if (form.mission) onLoadGoals(form.mission); }, [form.mission, onLoadGoals]);
  useEffect(() => { if (form.goal) onLoadActionItems(form.goal); }, [form.goal, onLoadActionItems]);

  const submit = async () => {
    if (!form.actionItem || !form.title.trim()) {
      alert('Action Item and Title are required.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosInstance.post('/api/vision/tasks', {
        actionItem: form.actionItem,
        title: form.title.trim(),
        deadline: form.deadline || undefined,
        assignee: form.assignee
      });
      onCreated(data.data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="v26-panel">
      <div className="v26-form">
        <div className="v26-form-row">
          <div className="v26-form-group" style={{ flex: 1 }}>
            <label>Mission *</label>
            <select value={form.mission}
              onChange={e => setForm(f => ({ ...f, mission: e.target.value, goal: '', actionItem: '' }))}>
              <option value="">{openMissions.length ? 'Select mission…' : 'No unlocked missions — unlock one first'}</option>
              {openMissions.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
            </select>
          </div>
          <div className="v26-form-group" style={{ flex: 1 }}>
            <label>Goal *</label>
            <select value={form.goal}
              onChange={e => setForm(f => ({ ...f, goal: e.target.value, actionItem: '' }))}
              disabled={!form.mission}>
              <option value="">{form.mission ? (goalList.length ? 'Select goal…' : 'No goals') : 'Pick mission first'}</option>
              {goalList.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
            </select>
          </div>
          <div className="v26-form-group" style={{ flex: 1 }}>
            <label>Action Item *</label>
            <select value={form.actionItem}
              onChange={e => setForm(f => ({ ...f, actionItem: e.target.value }))}
              disabled={!form.goal}>
              <option value="">{form.goal ? (aiList.length ? 'Select…' : 'No action items') : 'Pick goal first'}</option>
              {aiList.map(a => <option key={a._id} value={a._id}>{a.title}</option>)}
            </select>
          </div>
        </div>

        <div className="v26-form-group">
          <label>Task Title *</label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="What needs doing?" />
        </div>

        <div className="v26-form-row">
          <div className="v26-form-group" style={{ flex: 1 }}>
            <label>Deadline (optional)</label>
            <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
          </div>
          <div className="v26-form-group" style={{ flex: 2 }}>
            <label>Assignees</label>
            <AssigneePicker
              value={form.assignee}
              onChange={(arr) => setForm(f => ({ ...f, assignee: arr }))}
              systemUsers={systemUsers}
              placeholder="Add user or external…"
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="v26-btn v26-btn--secondary" onClick={onCancel}>Cancel</button>
          <button className="v26-btn v26-btn--primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyTracker;
